(ns ui.instagram-connect.page
  "Replicant page for the Instagram connect overview. Each account can
   get a fallback connection over Instagram's official API (the hidden
   INSTAGRAM platform of `app.bundle-social.platforms`), connected with
   the bundle.social connect flow, which returns to this page."
  (:require [app.bundle-social.page :as social-page]
            [parts.replicant.command :as command]
            [storrito.ui.core :as ui]
            [storrito.ui.button :as button]
            [storrito.ui.table :as ui-table]
            [parts.replicant.query :as query]
            [ui.app-frame :as app-frame]
            [ui.instagram-connect.oauth-return :as oauth-return]
            [storrito.instagram-connect.disconnect-reason :as reason]))

(def path
  "/ui/instagram-connect")

(def ig-connect-query
  {:query/kind :query/instagram-connect})

(defn connect-fallback-action
  "Connects the fallback of the `instagram-user`, which the hint modal
   asks to log in with, and notes its account type."
  [instagram-user]
  [:social-accounts/connect "INSTAGRAM" {:return-path path
                                          :username (:instagram-user/name instagram-user)
                                          :account-type (:instagram-user/account-type instagram-user)}])

(def account-type-labels
  "What a fallback connection of the account type cannot cover."
  {:creator {:label "Reels only"
             :title "Switch to Business for stories too."}
   :personal {:label "Needs a Business account"
              :title "Switch to Business in the Instagram app."}})

(defn- account-type-label
  [instagram-user]
  (when-let [{:keys [label title]} (account-type-labels (:instagram-user/account-type instagram-user))]
    ;; Only an icon next to the button: the cell keeps the row's height
    ;; and its width. Its tooltip shows at once on hover; it opens to
    ;; the left, since the table's box clips what leaves it and the
    ;; fallback is its last column.
    [:span {:class ["group" "relative" "cursor-help" "text-amber-600"]}
     [:i.fas.fa-triangle-exclamation]
     [:span.sr-only label]
     [:span {:class ["pointer-events-none" "absolute" "right-full" "top-1/2" "z-30"
                     "mr-2" "hidden" "w-max" "max-w-56" "-translate-y-1/2" "whitespace-normal"
                     "rounded-md" "bg-gray-900" "px-3" "py-2" "text-xs" "text-white"
                     "shadow-lg" "group-hover:block"]
             :aria-hidden "true"}
      [:span.font-semibold label] ". " title]]))

(defn calc-expire-progress
  [state]
  (let [expire-timestamp (:totp/expire-timestamp
                           (query/get-result state ig-connect-query))]
    (if expire-timestamp
      (-> (/ (- expire-timestamp
                (:ui/clock state))
             30000)
          (max 0)
          (min 1))
      1)))

(defn otp-cell
  "Masks the live code until it is revealed, so a screenshot or screenshare
  of the page does not expose valid codes. Copy works while masked."
  [state instagram-user]
  [:td {:class ui-table/td}
   (if-let [otp (:totp/otp instagram-user)]
     (let [progress (calc-expire-progress state)
           user-name (:instagram-user/name instagram-user)
           revealed? (get-in state [::revealed-otp user-name])]
       [:div.flex.items-center
        [:span.font-mono.font-bold.tracking-widest
         (if revealed? otp "••••••")]
        [:button.ml-2.text-gray-400.hover:text-gray-600
         {:on {:click [[:js/stop-propagation]
                       [:store/assoc-in [::revealed-otp user-name] (not revealed?)]]}
          :title (if revealed? "Hide code" "Show code")}
         [:i.fas {:class (if revealed? "fa-eye-slash" "fa-eye")}]]
        [:button.ml-2.text-gray-400.hover:text-gray-600
         {:on {:click [[:js/stop-propagation]
                       [:ig-account-modal/copy-otp otp]]}
          :title "Copy to clipboard"}
         [:i.fas.fa-copy]]
        [:div.w-10.bg-gray-100.rounded-3xl.h-2.5.ml-3
         [:div.rounded-3xl.transition-all.h-2.5
          {:class (if (< progress 0.2) "bg-red-400" "bg-indigo-600")
           :style {:width (str (* 100 progress) "%")}}]]])
     (button/button-secondary-xs
       {:type "button"
        :on {:click [[:js/stop-propagation]
                     [:ui/modal-open
                      :modals/instagram-account
                      {:username (:instagram-user/name instagram-user)}]]}
        :title "Setup 2FA for this account"}
       [:i.fas.fa-key.mr-1]
       "Setup"))])

(defn- remove-fallback-button
  [state account]
  (let [cmd (social-page/delete-command account)
        deleting? (command/issued? state cmd)]
    [:button.text-xs.text-gray-400.hover:text-red-600
     {:type "button"
      :disabled deleting?
      :title "Remove the fallback connection"
      :on {:click [[:js/stop-propagation]
                   [:js/confirm (str "Remove the fallback connection of "
                                     (:social.account/name account) "?")]
                   [:data/command cmd {:on-success [[:data/query ig-connect-query]]
                                       :on-error [[:ui/show-error]]}]]}}
     (if deleting?
       "Removing..."
       [:i.fas.fa-trash-can])]))

(defn fallback-cell
  "The fallback connection of the account: a button that adds it, its
   state, or a reconnect button when it was disconnected, and what the
   fallback cannot cover with the account's type."
  [state instagram-user]
  [:td {:class ui-table/td}
   [:div.flex.items-center.gap-3
    (if-let [account (:fallback/account instagram-user)]
      [:div.flex.items-center.gap-3
       (if (:social.account/disconnected-at account)
         [:button {:type "button"
                   :class button/button-danger-outline-sm-class
                   :on {:click [[:js/stop-propagation]
                                (connect-fallback-action instagram-user)]}}
          "Reconnect fallback"]
         [:span.inline-flex.items-center.px-2.py-1.rounded-md.text-sm.font-medium.bg-green-100.text-green-800
          {:title "Posts go out through the official Instagram API while the regular connection is disconnected."}
          [:i.fas.fa-shield-halved.text-green-600]
          [:div.hidden.sm:block.ml-2 "Active"]])
       (remove-fallback-button state account)]
      (button/button-secondary-xs
        {:type "button"
         :title "Add a fallback connection over Instagram's official API"
         :on {:click [[:js/stop-propagation]
                      (connect-fallback-action instagram-user)]}}
        [:i.fas.fa-shield-halved.mr-1]
        "Add"))
    (account-type-label instagram-user)]])

(defn account-row
  [state instagram-user]
  (let [user-name (:instagram-user/name instagram-user)
        blob-id (:instagram.user/profil-picture-blob-id instagram-user)
        img-src (if blob-id
                  (str "/image/" blob-id)
                  "/img/anonymous.png")]
    [:tr.cursor-pointer
     {:class ui-table/tr
      :on {:click [[:ui/modal-open
                    :modals/instagram-account
                    {:username user-name}]]}}
     [:td {:class ui-table/td}
      [:div.flex.items-center.text-sm.font-medium.text-gray-900
       [:div.h-7.w-7.sm:w-10.sm:h-10.mr-3
        [:img.rounded-full
         {:src img-src
          :alt ""}]]
       user-name]]

     [:td {:class ui-table/td}
      (if (:instagram.account/not-connected instagram-user)
        (let [disconnect-reason (:instagram.account/disconnect-reason instagram-user)
              label (reason/get-label disconnect-reason)]
          [:div.flex.flex-col.items-start.gap-1
           [:button {:on {:click [[:js/stop-propagation]
                                [:connect3-info/open
                                 {:username user-name
                                  :reconnect (:instagram.account/reconnect instagram-user)
                                  :source "accounts-page"}]]}
                     :class (concat button/button-danger-outline-sm-class
                                    ["font-medium"])}
            [:i.fas.fa-circle-exclamation.mr-2]
            "disconnected - reconnect now!"]
           (when disconnect-reason
             [:span.text-xs.text-gray-500 label])])

        [:span.inline-flex.items-center.px-2.py-1.rounded-md.text-sm.font-medium.bg-green-100.text-green-800
         [:i.fas.fa-check-circle.text-green-600]
         [:div.hidden.sm:block.ml-2 "Connected"]])]

     (otp-cell state instagram-user)
     (fallback-cell state instagram-user)]))

(defn empty-state
  []
  [:tr
   [:td {:colSpan 4}
    [:div.flex.flex-col.items-center.justify-center.py-12
     [:div.h-16.w-16.rounded-full.bg-gray-100.flex.items-center.justify-center.mb-4
      [:i.fab.fa-instagram.text-2xl.text-gray-400]]
     [:p.text-gray-500.mb-4 "No Instagram accounts connected yet"]
     [:button
      {:class button/button-primary-css
       :on {:click [[:connect3-info/open {:source "accounts-page"}]]}}
      [:i.fa.fa-user-plus.mr-2]
      "Connect your first account"]]]])

(defn account-table
  [state instagram-users]
  [:div {:class ui/box}
   [:table {:class ui-table/table}
    [:thead {:class ui-table/thead}
     [:tr
      [:th {:class ui-table/th
            :scope "col"}
       "Username"]
      [:th {:class ui-table/th
            :scope "col"}
       "Status"]
      [:th {:class ui-table/th
            :scope "col"}
       [:div.hidden.sm:block "2FA"]
       [:div.sm:hidden.block [:i.fas.fa-key]]]
      [:th {:class ui-table/th
            :scope "col"}
       [:div.hidden.sm:block "Fallback"]
       [:div.sm:hidden.block [:i.fas.fa-shield-halved]]]]]

    [:tbody {:class ui-table/tbody}
     (if (seq instagram-users)
       (map
         (fn [instagram-user]
           (account-row state instagram-user))
         instagram-users)
       (empty-state))]]])

(defn- syncing?
  [state]
  (boolean
    (when-let [team-id (::oauth-return/syncing-team state)]
      (command/issued? state (social-page/sync-command team-id)))))

(defn- fallback-notices
  "The progress and the errors of the fallback connect flow, and the
   fallback connections that belong to none of the accounts."
  [state unmatched]
  (list
    (when (syncing? state)
      [:div.mt-5.flex.items-center.gap-3.rounded-lg.border.border-blue-200.bg-blue-50.px-4.py-3.text-sm.text-blue-800
       {:replicant/key "syncing"}
       [:div.h-5.w-5.shrink-0.animate-spin.rounded-full.border-2.border-blue-200.border-t-blue-600]
       "Finishing the fallback connection. This takes a few seconds..."])
    (when-let [message (::oauth-return/callback-message state)]
      [:div.mt-5.text-sm.text-red-700.bg-red-50.border.border-red-200.rounded-lg.px-4.py-2
       {:replicant/key "callback-error"}
       message])
    (for [account unmatched]
      [:div.mt-5.flex.items-center.justify-between.gap-3.text-sm.text-amber-800.bg-amber-50.border.border-amber-200.rounded-lg.px-4.py-2
       {:replicant/key (str (:social.account/uuid account))}
       [:span
        (str (:social.account/name account)
             " is connected as a fallback, but it is not one of the Instagram accounts below. "
             "Remove it and log in with the same account in the Instagram dialog.")]
       (remove-fallback-button state account)])))

(defn panel
  [state]
  (let [result (query/get-result state ig-connect-query)
        instagram-users (:instagram-users result)
        ig-count (count instagram-users)]
    [:div.grow.p-4.overflow-y-auto
     [:div.container.m-auto
      [:div.flex.items-center.justify-between
       [:div
        [:h1.text-3xl.font-bold.text-gray-900 "Instagram Accounts"]
        (when (pos? ig-count)
          [:p.text-sm.text-gray-500.mt-1
           (str ig-count (if (= 1 ig-count) " account" " accounts") " connected")])]
       [:div.flex.gap-4
        (when (seq instagram-users)
          [:button
           {:class button/button-primary-css
            :on {:click [[:connect3-info/open {:source "accounts-page"}]]}}
           [:i.fa.fa-user-plus]
           [:p.ml-2.hidden.sm:block "Connect Account"]
           [:p.ml-2.sm:hidden "Add"]])
        [:button
         {:class button/button-secondary-css
          :on {:click [[:ui/modal-open :modal/connect-link-create]]}}
         [:i.fa.fa-link.mr-2]
         "Share a connect-link"]
        (when (seq instagram-users)
          [:ui/a.link
           {:ui/location
            {:location/page-id :pages/totp}
            :class button/button-secondary-css}
           [:i.fas.fa-key.mr-2]
           "2FA "
           [:p.ml-2.hidden.sm:block "Authenticators"]])]]

      (fallback-notices state (:fallback/unmatched-accounts result))

      [:div.w-full.mt-5
       (account-table state instagram-users)]]]))

(defn render [state]
  (app-frame/app-frame
    state
    (panel state)))

(defn on-load
  "Also handles the return from the OAuth of a fallback connection, see
   `ui.instagram-connect.oauth-return`."
  [location]
  (into [[:data/query ig-connect-query]
         [:totp/refresh]]
        (oauth-return/actions (:location/query-params location)
                              [[:data/query ig-connect-query]])))

(def page
  {:page-id :pages/instagram-connect
   ;; Keep in step with `path`.
   :route [["ui" "instagram-connect"]]
   :on-load #'on-load
   :render #'render})
